import { useState } from "react";
import { Plus, PackagePlus, ListPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { LineRow, LINE_GRID } from "@/components/quote/LineRow";
import { eur, lineFromItem, lineTotal, lineCost, newLineId } from "@/lib/pricing";
import { sectionColor } from "@/lib/constants";

const ItemPicker = ({ catalog, sections, onPick, sfx = "" }) => {
  const [open, setOpen] = useState(false);
  const groups = [...sections, ...new Set(catalog.map((i) => i.category).filter((c) => !sections.includes(c)))];
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button data-testid={`btn-add-section-item${sfx}`}><Plus className="h-4 w-4 mr-1" />Aggiungi dal listino</Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[min(420px,90vw)]" align="start">
        <Command>
          <CommandInput placeholder="Cerca articolo..." data-testid="item-picker-search" />
          <CommandList className="max-h-80">
            <CommandEmpty>Nessun articolo.</CommandEmpty>
            {groups.map((g) => {
              const items = catalog.filter((i) => i.category === g);
              if (!items.length) return null;
              return (
                <CommandGroup key={g} heading={g}>
                  {items.map((i) => (
                    <CommandItem key={i.id} value={`${i.name} ${g}`} onSelect={() => { onPick(i); setOpen(false); }} data-testid={`item-pick-${i.id}`}>
                      <span className="flex-1 truncate">{i.name}</span>
                      <span className="font-mono text-xs text-slate-400">{eur(i.price)}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              );
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const KitPicker = ({ kits, onPick, sfx = "" }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="secondary" data-testid={`btn-add-kit${sfx}`}><PackagePlus className="h-4 w-4 mr-1" />Aggiungi pacchetto</Button>
      </PopoverTrigger>
      <PopoverContent className="p-1 w-[min(380px,90vw)]" align="start">
        {kits.length === 0 && <p className="p-3 text-sm text-slate-400">Nessun pacchetto. Creali in Catalogo & Kit → Pacchetti.</p>}
        {kits.map((k) => (
          <button key={k.id} onClick={() => { onPick(k); setOpen(false); }} className="w-full text-left p-3 rounded hover:bg-slate-800 transition-colors" data-testid={`kit-pick-${k.id}`}>
            <div className="font-medium text-sm">{k.name}</div>
            <div className="text-xs text-slate-400">{k.items.length} articoli · {k.description}</div>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
};

const Section = ({ title, lines, q, sections, catalog, onChange, onRemove, onMove }) => (
  <div className="border border-slate-800 rounded-lg" data-testid={`section-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
    <div className="flex items-center justify-between px-4 py-2 bg-slate-800/50 border-l-4" style={{ borderLeftColor: sectionColor(title) }}>
      <span className="font-mono text-xs font-semibold tracking-wider">{title}</span>
      <span className="font-mono text-xs text-slate-400">{title === "OMAGGIO" ? "valore indicativo" : eur(lines.reduce((s, l) => s + lineTotal(l, q.days), 0))}<span className="text-cyan-300 ml-3">costo {eur(lines.reduce((s, l) => s + lineCost(l, q.days), 0))}</span></span>
    </div>
    <div className={`hidden lg:grid ${LINE_GRID} gap-2.5 px-3 pt-2 micro-label`}>
      <span>Qt</span><span>Descrizione</span><span>Giorni</span><span>{title === "OMAGGIO" ? "Valore €" : "Prezzo/gg €"}</span><span>Calcolo</span><span className="text-cyan-300">Costo €</span><span className="text-cyan-300">Calc. costo</span><span className="text-right">Totale</span><span className="text-right text-cyan-300">Costo</span><span />
    </div>
    <div className="divide-y divide-slate-800">
      {lines.map((l, i) => <LineRow key={l.id} line={l} quoteDays={q.days} sections={sections} catalog={catalog} onChange={onChange} onRemove={onRemove} onMove={onMove} first={i === 0} last={i === lines.length - 1} />)}
    </div>
  </div>
);

export const LinesEditor = ({ q, setLines, data }) => {
  const sections = data.settings.sections;
  const regular = q.lines.filter((l) => !l.omaggio);
  const omaggi = q.lines.filter((l) => l.omaggio);
  const order = [...sections, ...new Set(regular.map((l) => l.category).filter((c) => !sections.includes(c)))];
  const byId = Object.fromEntries(data.catalog.map((i) => [i.id, i]));
  const onChange = (id, patch) => setLines((ls) => ls.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  const onRemove = (id) => setLines((ls) => ls.filter((l) => l.id !== id));
  const sameGroup = (a, b) => !!a.omaggio === !!b.omaggio && (a.omaggio || a.category === b.category);
  const onMove = (id, dir) => setLines((ls) => {
    const i = ls.findIndex((l) => l.id === id);
    let j = i + dir;
    while (j >= 0 && j < ls.length && !sameGroup(ls[i], ls[j])) j += dir;
    if (i < 0 || j < 0 || j >= ls.length) return ls;
    const out = [...ls];
    [out[i], out[j]] = [out[j], out[i]];
    return out;
  });
  const addKit = (k) => setLines((ls) => [...ls, ...k.items.filter((ki) => byId[ki.item_id]).map((ki) => lineFromItem(byId[ki.item_id], ki.qty))]);
  const addBlank = () => setLines((ls) => [...ls, { id: newLineId(), item_id: null, category: sections[0], name: "", details: "", qty: 1, days: null, price: 0, mode: "radice", omaggio: false, value: 0, cost: 0, cost_mode: "radice", source: "proprio" }]);
  const props = { q, sections, catalog: data.catalog, onChange, onRemove, onMove };
  const actions = (sfx) => (
    <>
      <ItemPicker catalog={data.catalog} sections={sections} sfx={sfx} onPick={(i) => setLines((ls) => [...ls, lineFromItem(i)])} />
      <KitPicker kits={data.kits} onPick={addKit} sfx={sfx} />
      <Button variant="outline" onClick={addBlank} data-testid={`btn-add-blank-line${sfx}`}><ListPlus className="h-4 w-4 mr-1" />Riga libera</Button>
    </>
  );

  return (
    <section className="bg-slate-900 border border-slate-800 rounded-lg p-4 sm:p-5 space-y-4" data-testid="lines-editor">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-base md:text-lg font-semibold mr-auto">Voci del preventivo</h2>
        {actions("")}
      </div>
      {q.lines.length === 0 && (
        <div className="border border-dashed border-slate-700 rounded-lg p-10 text-center text-sm text-slate-400" data-testid="lines-empty">
          Nessuna voce. Usa la composizione AI, il listino o un kit per iniziare.
        </div>
      )}
      {order.map((sec) => {
        const ls = regular.filter((l) => l.category === sec);
        return ls.length ? <Section key={sec} title={sec} lines={ls} {...props} /> : null;
      })}
      {omaggi.length > 0 && <Section title="OMAGGIO" lines={omaggi} {...props} />}
      {q.lines.length > 0 && (
        <div className="flex flex-wrap items-center justify-end gap-2 pt-2 border-t border-slate-800" data-testid="lines-bottom-actions">
          {actions("-bottom")}
        </div>
      )}
    </section>
  );
};
